'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { firePlatformToast } from '@/lib/platform-toast';
import {
  ArrowRight,
  Check,
  Copy,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Scissors,
  User,
} from 'lucide-react';
import { registerSchema, type RegisterInput } from '@/lib/validators';
import { authApi } from '@/lib/api';
import { buildTenantUrl, PLATFORM_DOMAIN, PLATFORM_NAME } from '@/lib/branding';
import type { AuthBranding } from '@/lib/auth-branding';
import { Button } from '@/components/ui/generics/data-display/button/Button';
import { Badge } from '@/components/ui/personalized/feedback/badge/Badge';
import { FormField } from '@/components/ui/generics/inputs/form-field/FormField';
import { FormGrid } from '@/components/ui/generics/inputs/form-grid/FormGrid';
import { Input } from '@/components/ui/generics/inputs/input/Input';
import { StepIndicator } from '@/components/ui/generics/data-display/step-indicator/StepIndicator';
import { PhoneInput } from '@/components/ui/generics/inputs/phone-input/PhoneInput';
import { unformatPhone } from '@/lib/format';
import {
  GoogleButton,
  useGoogleOAuthEnabled,
} from '@/components/public/auth/GoogleButton';
import { PasswordStrength } from '@/components/public/auth/PasswordStrength';
import { SlugPreview } from '@/components/public/auth/SlugPreview';
import {
  AuthSheet,
  AuthShell,
  AuthSurface,
  Logo7b,
  Tag,
  OrDivider,
  SectionLabel,
  TrialPreview,
  Testimonial,
} from '@/components/public/auth/ds';
import { useIsMobileViewport } from '@/lib/use-mobile-viewport';
import { ArrowLeft } from 'lucide-react';

const HCAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_HCAPTCHA_SITE_KEY?.trim();

declare global {
  interface Window {
    hcaptcha?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          theme?: 'dark' | 'light';
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        },
      ) => string;
      reset: (widgetId?: string) => void;
    };
    onHCaptchaLoad?: () => void;
  }
}

export function RegisterClient({
  branding,
  logoUrl = null,
  logoAlt,
}: {
  branding: AuthBranding;
  logoUrl?: string | null;
  logoAlt?: string;
}) {
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [registered, setRegistered] = useState<{
    email: string;
    sent: boolean;
    required: boolean;
    shop: string;
    name: string;
    slug: string;
  } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const captchaContainerRef = useRef<HTMLDivElement | null>(null);
  const captchaWidgetIdRef = useRef<string | null>(null);
  // Divider "OU CADASTRE COM E-MAIL" so aparece quando o botao Google existe
  // (sem credencial Google o "OU" ficava orfao na tela).
  const googleEnabled = useGoogleOAuthEnabled();
  // MOBILE-FIRST: o wizard vive num Drawer bottom (sheet, padrao da agenda).
  // Nasce FECHADO - abre apenas pelo CTA "Criar minha conta" da pagina.
  const isMobile = useIsMobileViewport();
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    if (!HCAPTCHA_SITE_KEY || !captchaContainerRef.current) return;

    const renderWidget = () => {
      if (
        !window.hcaptcha ||
        !captchaContainerRef.current ||
        captchaWidgetIdRef.current
      )
        return;
      captchaWidgetIdRef.current = window.hcaptcha.render(
        captchaContainerRef.current,
        {
          sitekey: HCAPTCHA_SITE_KEY,
          theme: 'light',
          callback: (token) => setCaptchaToken(token),
          'expired-callback': () => setCaptchaToken(null),
          'error-callback': () => setCaptchaToken(null),
        },
      );
    };

    if (window.hcaptcha) {
      renderWidget();
      return;
    }

    if (!document.querySelector('script[data-hcaptcha]')) {
      const script = document.createElement('script');
      script.src =
        'https://js.hcaptcha.com/1/api.js?onload=onHCaptchaLoad&render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.hcaptcha = '1';
      window.onHCaptchaLoad = renderWidget;
      document.head.appendChild(script);
    }
  }, []);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    trigger,
    formState: { errors, isSubmitting, submitCount },
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      acceptTerms: false as unknown as true,
      passwordConfirm: '',
    } as Partial<RegisterInput>,
  });

  const password = watch('password') ?? '';
  const barbershopName = watch('barbershopName') ?? '';
  const slug = watch('slug') ?? '';
  const phone = watch('phone') ?? '';

  // Wizard mobile: campos por step. Desktop ignora estes states e mostra
  // todas as sections de uma vez (CSS @media revela tudo).
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const stepFields: Array<Array<keyof RegisterInput>> = [
    ['name', 'email'],
    ['password', 'passwordConfirm'],
    ['barbershopName', 'phone', 'acceptTerms'],
  ];

  // O zodResolver valida o schema INTEIRO em qualquer trigger e o RHF grava
  // erros de campos que o usuário ainda nem viu ("Nome da barbearia
  // obrigatório" aparecia no wizard antes do step 3). O gate é de EXIBIÇÃO:
  // erro só aparece se o step do campo já foi VALIDADO pelo usuário
  // (Continuar clicado nele) ou após tentar submeter (cobre o desktop,
  // que mostra as 3 seções de uma vez).
  const [validatedStep, setValidatedStep] = useState(-1);
  const stepOfField = (name: keyof RegisterInput) =>
    stepFields.findIndex((fields) => fields.includes(name));
  const visibleError = (name: keyof RegisterInput): string | undefined =>
    stepOfField(name) <= validatedStep || submitCount > 0
      ? (errors[name]?.message as string | undefined)
      : undefined;

  const goNext = async () => {
    setValidatedStep((current) => Math.max(current, step));
    const ok = await trigger(stepFields[step], { shouldFocus: true });
    if (!ok) return;
    if (step < 2) setStep((step + 1) as 0 | 1 | 2);
  };
  const goBack = () => {
    if (step > 0) setStep((step - 1) as 0 | 1 | 2);
  };

  const onSubmit = async (data: RegisterInput) => {
    if (HCAPTCHA_SITE_KEY && !captchaToken) {
      void firePlatformToast('toast.error.captcha-required', {
        fallback: 'Confirme o captcha antes de continuar.',
      });
      return;
    }

    try {
      const res = await authApi.register({
        name: data.name,
        email: data.email,
        password: data.password,
        barbershopName: data.barbershopName,
        slug: data.slug?.trim() || undefined,
        phone: data.phone ? unformatPhone(data.phone) || undefined : undefined,
        acceptTerms: data.acceptTerms,
        captchaToken: captchaToken ?? undefined,
      });
      const body = res.data;
      const sent = !!body.emailVerificationSent;
      const required = !!body.emailVerificationRequired;
      const createdTenantSlug =
        ((body.user as { tenant?: { slug?: string } } | undefined)?.tenant
          ?.slug as string | undefined) ??
        data.slug?.trim() ??
        '';

      if (required && !sent) {
        void firePlatformToast('toast.warning.account-created-email-failed', {
          fallback:
            'Conta criada, mas não conseguimos enviar o e-mail. Verifique com o operador.',
        });
      } else if (sent) {
        void firePlatformToast('toast.success.account-created-pending-verification', {
          fallback: 'Conta criada! Confirme seu e-mail para ativar.',
        });
      } else {
        void firePlatformToast('toast.success.account-created-welcome', {
          vars: { platformName: PLATFORM_NAME },
          fallback: `Conta criada com sucesso! Bem-vindo ao ${PLATFORM_NAME}.`,
        });
      }

      setRegistered({
        email: data.email,
        sent,
        required,
        shop: data.barbershopName,
        name: data.name,
        slug: createdTenantSlug,
      });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Erro ao criar conta. Tente novamente.';
      void firePlatformToast('toast.error.generic', {
        textOverride: message,
        fallback: message,
      });
      if (HCAPTCHA_SITE_KEY && window.hcaptcha) {
        window.hcaptcha.reset();
        setCaptchaToken(null);
      }
    }
  };

  // ─ Tela de sucesso pos-cadastro tradicional ────────────────────────────
  if (registered) {
    const tenantUrl = registered.slug
      ? `${registered.slug}.${PLATFORM_DOMAIN}`
      : null;
    const tenantLoginHref = registered.slug
      ? buildTenantUrl(registered.slug, '/login')
      : null;

    const copyTenantLink = async () => {
      if (!tenantLoginHref) return;
      try {
        await navigator.clipboard.writeText(tenantLoginHref);
        setLinkCopied(true);
        window.setTimeout(() => setLinkCopied(false), 2500);
      } catch {
        // clipboard indisponivel (http/permissao) - o link segue visivel na tela
      }
    };

    return (
      <AuthShell>
        <AuthSurface striped={false} lg className="auth-page-register">
          <div className="auth-page-register__grid auth-page-register__grid--single">
            <section className="auth-page-register__form">
              <div className="auth-page-register__form-inner auth-page-register__form-inner--complete">
                <div className="auth-page-register__complete" role="status" aria-live="polite">
                  <span className="auth-page-register__complete-mark">
                    <Check size={28} strokeWidth={2.4} aria-hidden="true" />
                  </span>
                  <Tag dot tone="ok">Conta criada</Tag>
                  <h2 className="auth-display auth-display--md auth-page-register__title">
                    {registered.sent
                      ? <>Confirme seu <em>e-mail</em> para começar.</>
                      : <>Sua barbearia <em>está pronta.</em></>}
                  </h2>
                  {/* Copy minima: tela boa nao precisa de explicacao longa -
                      o rotulo do box + o CTA ja dizem o que fazer. */}
                  <p className="auth-subtitle">
                    {registered.sent ? (
                      <>
                        Enviamos um link de ativação para{' '}
                        <strong>{registered.email}</strong>. Confirme em até 7 dias.
                      </>
                    ) : tenantUrl ? (
                      'Guarde o endereço: é por ele que você entra no painel.'
                    ) : (
                      'Entre com o e-mail e a senha cadastrados.'
                    )}
                  </p>

                  {tenantUrl && (
                    <div className="auth-page-register__tenant-link">
                      <span className="auth-page-register__tenant-link-label">
                        Endereço da sua barbearia
                      </span>
                      <code className="auth-page-register__tenant-link-url">
                        {tenantUrl}
                      </code>
                    </div>
                  )}

                  <div className="auth-page-register__complete-actions">
                    {!registered.sent && tenantLoginHref && (
                      <a
                        className="btn btn--primary btn--lg"
                        href={tenantLoginHref}
                      >
                        <span>Acessar minha barbearia</span>
                        <ArrowRight size={16} aria-hidden="true" />
                      </a>
                    )}
                    {tenantLoginHref && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="lg"
                        onClick={copyTenantLink}
                        leftIcon={
                          linkCopied ? (
                            <Check size={16} aria-hidden="true" />
                          ) : (
                            <Copy size={16} aria-hidden="true" />
                          )
                        }
                      >
                        {linkCopied ? 'Link copiado!' : 'Copiar link'}
                      </Button>
                    )}
                  </div>

                  <dl className="auth-page-register__summary">
                    <div>
                      <dt>Responsável</dt>
                      <dd>{registered.name}</dd>
                    </div>
                    <div>
                      <dt>E-mail</dt>
                      <dd>{registered.email}</dd>
                    </div>
                    <div>
                      <dt>Barbearia</dt>
                      <dd>{registered.shop}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </section>
          </div>
        </AuthSurface>
      </AuthShell>
    );
  }

  // ─ Form de cadastro ────────────────────────────────────────────────────
  return (
    <AuthShell>
      <AuthSurface striped={false} lg className="auth-page-register">
        <div className="auth-page-register__grid">
          {/* Brand panel (esquerda) */}
          <aside className="auth-page-register__brand" aria-hidden="true">
            <div className="auth-page-register__brand-head">
              {/* Caption = par titulo/subtitulo nos DOIS viewports (decisao
                  do operador 2026-07-07) - sem eyebrow duplicando a mesma
                  informacao acima do hero. */}
              <Logo7b
                size="lg"
                src={logoUrl}
                alt={logoAlt}
                caption="Comece em 1 minuto"
              />
              {/* Badge de trial no cabecalho e DESKTOP-only: no mobile ele
                  estourava a linha em aparelhos estreitos/fonte ampliada
                  (falha real em producao 2026-07-07) - la ele vive DENTRO
                  do cartao, abaixo do CTA. */}
              <Badge tone="success" className="auth-page-register__trial-badge">
                {branding.trialDays} dias grátis · sem cartão
              </Badge>
            </div>

            <div>
              <h1 className="auth-display auth-display--xl auth-page-register__hero auth-page-register__hero-desktop">
                Comece grátis.
                <br />
                <em>Cancele quando quiser.</em>
              </h1>
              {/* Sem repeticao: "1 minuto" vive na caption e "sem cartao"
                  no badge de trial. */}
              <p className="auth-subtitle auth-subtitle--lg auth-page-register__subtitle-desktop">
                Teste todos os recursos gratuitamente.
              </p>
            </div>

            <TrialPreview />

            <Testimonial
              quote="Em 2 semanas a agenda lotou. O WhatsApp confirma sozinho - eu só corto."
              author="João Ricardo"
              meta="Barbearia do João, Curitiba"
            />

            <p className="auth-microcopy auth-page-register__brand-foot">
              <span>sem cartão</span>
              <span className="auth-microcopy__sep">·</span>
              <span>cancele em 1 clique</span>
              <span className="auth-microcopy__sep">·</span>
              <span>suporte humano</span>
            </p>
          </aside>

          {/* Form panel (direita). Desktop: painel inline; mobile: Drawer
              bottom (sheet) com o wizard - CTA na página reabre se dispensado.
              No mobile o launcher vive num CARTAO (mesma moldura do /login),
              com o badge de trial abaixo do CTA. */}
          <section className="auth-page-register__form">
            <div className="auth-page-register__launcher">
              <AuthSheet
              isMobile={isMobile}
              open={sheetOpen}
              onOpen={() => setSheetOpen(true)}
              onClose={() => setSheetOpen(false)}
              launcherLabel="Criar minha conta"
              closeLabel="Fechar cadastro"
              inlineClassName="auth-page-register__form-inner"
              icon={
                [
                  <User key="i" size={20} aria-hidden="true" />,
                  <Lock key="i" size={20} aria-hidden="true" />,
                  <Scissors key="i" size={20} aria-hidden="true" />,
                ][step]
              }
            >
              {/* Titulo global (so desktop): "Crie sua conta no 7barber." */}
              <h2 className="auth-display auth-display--md auth-page-register__title auth-page-register__title-desktop">
                Crie sua conta <em>no 7barber.</em>
              </h2>

              {/* Step header mobile - StepIndicator do catalogo (numerado e
                  rotulado; passo concluido e clicavel pra revisar sem perder
                  o estado) + h1 dinamico por step. O eyebrow "PASSO X DE 3"
                  saiu: o indicador ja diz posicao e total. Aria-live polite
                  pra screen reader anunciar troca de step. */}
              <div
                className="auth-page-register__step-header"
                aria-live="polite"
              >
                <StepIndicator
                  className="auth-page-register__steps"
                  steps={[
                    { label: 'Você' },
                    { label: 'Acesso' },
                    { label: 'Barbearia' },
                  ]}
                  currentStep={step + 1}
                  onStepClick={(stepNum) => setStep((stepNum - 1) as 0 | 1 | 2)}
                />
                {/* So o titulo - sem subtitulo de passo: era texto filler
                    (o requisito de senha ja vive no medidor; o trial ja
                    vive no badge da pagina). Mobile sem poluicao visual. */}
                <h1 className="auth-display auth-display--md auth-page-register__step-title">
                  {step === 0 && <>Quem é<br /><em>você?</em></>}
                  {step === 1 && <>Crie um<br /><em>acesso</em> seguro.</>}
                  {step === 2 && <>Sobre sua<br /><em>barbearia.</em></>}
                </h1>
              </div>

              <FormGrid gap="sm" className="auth-page-register__stack" data-step={step}>
                {googleEnabled === true && (
                  <FormGrid gap="sm" className="auth-page-register__google-section">
                    <GoogleButton label="Continuar com Google" enabled />
                    <OrDivider label="OU CADASTRE COM E-MAIL" />
                  </FormGrid>
                )}

                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="auth-page-register__form-stack form-grid form-grid--gap-sm"
                  data-step={step}
                  noValidate
                >
                  {/* ── Section 1: Você ──────────────────────────────────── */}
                  <FormGrid gap="sm" className="auth-page-register__step auth-page-register__step--1">
                    <SectionLabel index={1} title="Você" />
                    {/* FormGrid (organizador do catalogo): 2 colunas quando
                        cabem, colapso automatico no estreito - sem grid cru. */}
                    <FormGrid columns={2} gap="sm">
                      <FormField
                        error={visibleError('name')}
                        htmlFor="name"
                      >
                        <Input
                          id="name"
                          type="text"
                          floatingLabel="Seu nome"
                          autoComplete="name"
                          prefixIcon={<User size={16} aria-hidden="true" />}
                          error={visibleError('name')}
                          aria-label="Seu nome"
                          {...register('name')}
                        />
                      </FormField>
                      <FormField
                        error={visibleError('email')}
                        htmlFor="email"
                      >
                        <Input
                          id="email"
                          type="email"
                          floatingLabel="E-mail"
                          autoComplete="email"
                          prefixIcon={<Mail size={16} aria-hidden="true" />}
                          error={visibleError('email')}
                          aria-label="E-mail"
                          {...register('email')}
                        />
                      </FormField>
                    </FormGrid>
                  </FormGrid>

                  {/* ── Section 2: Acesso ────────────────────────────────── */}
                  <FormGrid gap="sm" className="auth-page-register__step auth-page-register__step--2">
                    {/* Sem `sub`: o requisito de senha ja aparece (e reage ao
                        digitar) no PasswordStrength logo abaixo do campo. */}
                    <SectionLabel index={2} title="Acesso" />
                    <FormGrid gap="sm">
                    <FormField
                      error={visibleError('password')}
                      htmlFor="password"
                    >
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        floatingLabel="Senha"
                        autoComplete="new-password"
                        prefixIcon={<Lock size={16} aria-hidden="true" />}
                        suffixIcon={
                          <button
                            type="button"
                            onClick={() => setShowPassword((value) => !value)}
                            aria-label={
                              showPassword ? 'Ocultar senha' : 'Mostrar senha'
                            }
                            tabIndex={-1}
                          >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        }
                        error={visibleError('password')}
                        aria-label="Senha"
                        {...register('password')}
                      />
                      <PasswordStrength password={password} />
                    </FormField>
                    <FormField
                      error={visibleError('passwordConfirm')}
                      htmlFor="passwordConfirm"
                    >
                      <Input
                        id="passwordConfirm"
                        type={showPassword ? 'text' : 'password'}
                        floatingLabel="Confirmar senha"
                        autoComplete="new-password"
                        prefixIcon={<Lock size={16} aria-hidden="true" />}
                        error={visibleError('passwordConfirm')}
                        aria-label="Confirmar senha"
                        {...register('passwordConfirm')}
                      />
                    </FormField>
                    </FormGrid>
                  </FormGrid>

                  {/* ── Section 3: Sua barbearia ─────────────────────────── */}
                  <FormGrid gap="sm" className="auth-page-register__step auth-page-register__step--3">
                    <SectionLabel index={3} title="Sua barbearia" />
                    {/* FormGrid: 2 colunas quando cabem, colapso automatico. */}
                    <FormGrid columns={2} gap="sm">
                      <FormField
                        error={visibleError('barbershopName')}
                        htmlFor="barbershopName"
                      >
                        <Input
                          id="barbershopName"
                          type="text"
                          floatingLabel="Nome da barbearia"
                          prefixIcon={<Scissors size={16} aria-hidden="true" />}
                          error={visibleError('barbershopName')}
                          aria-label="Nome da barbearia"
                          {...register('barbershopName')}
                        />
                        <SlugPreview
                          barbershopName={barbershopName}
                          rootDomain={PLATFORM_DOMAIN}
                          value={slug}
                          onChange={(next) =>
                            setValue('slug', next, { shouldDirty: true })
                          }
                        />
                      </FormField>
                      <FormField
                        error={visibleError('phone')}
                        htmlFor="phone"
                        helperText="Opcional · acelera o setup do chatbot."
                      >
                        <PhoneInput
                          id="phone"
                          autoComplete="tel"
                          floatingLabel="WhatsApp"
                          value={phone}
                          onChange={(next) =>
                            setValue('phone', next, { shouldDirty: true })
                          }
                          error={visibleError('phone')}
                        />
                      </FormField>
                    </FormGrid>

                    <Controller
                      control={control}
                      name="acceptTerms"
                      render={({ field }) => (
                        <label className="check auth-page-register__terms">
                          <input
                            type="checkbox"
                            checked={!!field.value}
                            onChange={(event) =>
                              field.onChange(event.target.checked)
                            }
                          />
                          <span className="check__label">
                            Li e aceito os{' '}
                            <Link href="/terms" target="_blank" className="link">
                              Termos
                            </Link>{' '}
                            e a{' '}
                            <Link href="/privacy" target="_blank" className="link">
                              Política de Privacidade
                            </Link>.
                          </span>
                        </label>
                      )}
                    />
                    {visibleError('acceptTerms') && (
                      <div className="field__error field__error--tight">
                        {visibleError('acceptTerms')}
                      </div>
                    )}

                    {HCAPTCHA_SITE_KEY && (
                      <div ref={captchaContainerRef} className="auth-page-register__captcha" />
                    )}
                  </FormGrid>

                  {/* CTA desktop: sempre visivel, submete o form inteiro. */}
                  <div className="auth-page-register__cta auth-page-register__cta--desktop">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      fullWidth
                      loading={isSubmitting}
                      disabled={!!HCAPTCHA_SITE_KEY && !captchaToken}
                      rightIcon={<ArrowRight size={16} aria-hidden="true" />}
                    >
                      Criar minha conta
                    </Button>
                  </div>

                  {/* CTA mobile: navega entre steps; no step final, submete. */}
                  <div className="auth-page-register__cta auth-page-register__cta--mobile">
                    {step > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="lg"
                        onClick={goBack}
                        leftIcon={<ArrowLeft size={16} aria-hidden="true" />}
                      >
                        Voltar
                      </Button>
                    )}
                    {step < 2 ? (
                      /* key distinta: sem ela o React REAPROVEITA o nó DOM e o
                         botão vira type=submit no meio do clique do Continuar,
                         disparando submissão fantasma (setava os erros de
                         todos os campos antes do usuário chegar no step 3). */
                      <Button
                        key="wizard-next"
                        type="button"
                        variant="primary"
                        size="lg"
                        fullWidth
                        onClick={goNext}
                        rightIcon={<ArrowRight size={16} aria-hidden="true" />}
                      >
                        Continuar
                      </Button>
                    ) : (
                      <Button
                        key="wizard-submit"
                        type="submit"
                        variant="primary"
                        size="lg"
                        fullWidth
                        loading={isSubmitting}
                        disabled={!!HCAPTCHA_SITE_KEY && !captchaToken}
                        rightIcon={<ArrowRight size={16} aria-hidden="true" />}
                      >
                        Criar minha conta
                      </Button>
                    )}
                  </div>

                </form>
              </FormGrid>

            </AuthSheet>

              {/* Badge de trial DENTRO do cartao, abaixo do CTA (mobile-only
                  via CSS) - fora da linha do cabecalho, nunca estoura. */}
              <div className="auth-page-register__trial-badge-mobile">
                <Badge tone="success">
                  {branding.trialDays} dias grátis · sem cartão
                </Badge>
              </div>
            </div>
          </section>
        </div>
      </AuthSurface>
    </AuthShell>
  );
}
